import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { errorLog } from '@bitComponents/utils/logUtils';
import {
  ICONS_ID,
  COLOR as ICON_COLOR,
} from '@bitComponents/unifiedIcons/SvgIconLoader';
import {
  applyTransform2Object,
  normalizeString,
  toKebabCase,
} from '@bitComponents/utils/common';
/* THEMES */
import { isNovum } from '@bitComponents/telcovid/CommonUtils';
import isColApp from '@gcloud-npm/utils.common.is-colapp';
import Header from '../../../bitComponents/Header';
import BeachTheme from './Themes/Beach/Beach';
import Loading from './Themes/Loading';
import NetworkErrorTheme from './Themes/NetworkError';
/* CONTENT */
import InfoContent, { CONTENT_KIND } from './Content/InfoContent';
import Alert from './Content/Alert';
import './Stopper.scss';

export const THEMES = {
  BEACH: 'beach',
  EMPTY: 'empty',
  LOADING: 'loading',
  NETWORK_ERROR: 'networkError',
};

export const CONTENT = {
  NOT_FOUND: 'NotFound',
  ERROR: 'FatalError',
  ERROR_ALERT: 'ErrorAlert',
  INFO_ALERT: 'InfoAlert',
  USER_ALERT: 'UserAlert',
  NONE: 'None',
};

export const ERROR_REQUIRED_KEYS = ['eventType', 'errorDetail', 'errorOrigin'];

/**
 * Crea el objeto con la estructura adecuada para un evento de componente de métricas
 * @param {String} eventType
 * @param {String} eventType
 * @param {String} errorDetail
 * @param {String} errorOrigin
 */
export const createErrorEventObject = (inputObj) => {
  const finalRes = {
    event: 'cms-error',
    ...inputObj,
  };

  const normalizedStructure = applyTransform2Object(
    [
      {
        type: 'string',
        method: str => normalizeString(toKebabCase(str)),
      },
    ],
    keyName => toKebabCase(keyName),
    finalRes,
  );
  return normalizedStructure;
};

export const checkErrorEventObj = (taggingInfo) => {
  let res = true;
  ERROR_REQUIRED_KEYS.forEach((key) => {
    if (typeof taggingInfo[key] === 'undefined') {
      res = false;
    }
  });
  return res;
};

export const sendCMSErrorEvent = (inputObj) => {
  const checkObj = checkErrorEventObj(inputObj);
  const object = createErrorEventObject(inputObj);

  try {
    if (checkObj) {
      window.nookInfoExchange.sendMessage('Metrics', object);
    } else {
      errorLog(
        'TaggingInfo is no properly defined',
        '[checkEventObj] no right event structure',
      );
    }
  } catch (err) {
    // TODO monirorizar donde toque.
  }
};

const themeLoader = (theme, retry) => {
  switch (theme) {
    case THEMES.EMPTY:
      return null;
    case THEMES.LOADING:
      return <Loading />;
    case THEMES.NETWORK_ERROR:
      return <NetworkErrorTheme retry={retry} />;
    case THEMES.BEACH:
    default:
      return <BeachTheme />;
  }
};

const getInPageErrorDetails = (contentProps) => {
  const { errorName, errorCode, errorDetails } = contentProps;
  let errorDetail = `${errorName}`;
  if (errorCode) {
    errorDetail = `${errorDetail}-${errorCode}`;
  }
  if (errorDetails) {
    errorDetail = `${errorDetail}-${errorDetails}`;
  }
  return errorDetail;
};

const contentLoader = (content, contentProps, projectId, taggingInfo) => {
  let contentToLoad = null;
  let errorDetail = '';
  const finalTaggingInfo = {
    ...taggingInfo,
    componentVariation: taggingInfo.componentVariation || 'all',
    componentProduct: taggingInfo.componentProduct || '',
    componentName: taggingInfo.componentName || `Stopper;${content}`,
  };
  switch (content) {
    case CONTENT.ERROR:
      contentToLoad = (
        <InfoContent kind={CONTENT_KIND.ERROR} taggingInfo={finalTaggingInfo} />
      );
      errorDetail = 'FatalError-500-ErrorJs';
      break;
    case CONTENT.ERROR_ALERT: {
      contentToLoad = (
        <Alert
          iconColor={ICON_COLOR.PIPPER}
          iconId={ICONS_ID.Alerta}
          errorToPrint={contentProps}
          taggingInfo={finalTaggingInfo}
        />
      );
      errorDetail = getInPageErrorDetails(contentProps);
      break;
    }
    case CONTENT.INFO_ALERT:
      contentToLoad = (
        <Alert
          iconColor={ICON_COLOR.BLUE_A}
          iconId={ICONS_ID.Informacion}
          errorToPrint={contentProps}
          taggingInfo={finalTaggingInfo}
        />
      );
      errorDetail = getInPageErrorDetails(contentProps);
      break;
    case CONTENT.USER_ALERT:
      contentToLoad = (
        <Alert
          iconColor={ICON_COLOR.BLUE_A}
          iconId={ICONS_ID.Seguridad}
          errorToPrint={contentProps}
          taggingInfo={finalTaggingInfo}
        />
      );
      errorDetail = getInPageErrorDetails(contentProps);
      break;
    case CONTENT.NONE:
      contentToLoad = null;
      break;
    default:
      contentToLoad = <InfoContent taggingInfo={finalTaggingInfo} />;
      errorDetail = 'NotFound-404';
      break;
  }
  sendCMSErrorEvent({
    eventType: 'error-page',
    errorDetail,
    errorOrigin: projectId,
  });

  return contentToLoad;
};

/* iF CHILDREN IS DEFINED WE IGNORE CONTENT */
const Stopper = ({
  children,
  theme,
  content,
  contentProps,
  projectId,
  taggingInfo,
  contextData,
  retry,
}) => {
  const finalContent = children ? null : content;
  console.log('status: ', { theme, content });
  return (
    <Fragment>
      {!isNovum() && !isColApp() && <Header />}
      <div className={`Stopperito ${content} ${theme}`}>
        <div className="childrenContainer">
          {children && children}
          {!children
          && finalContent
          && contentLoader(
            finalContent,
            contentProps,
            projectId,
            taggingInfo,
            contextData,
          )}
        </div>
        {themeLoader(theme, retry)}
      </div>
    </Fragment>
  );
};

Stopper.propTypes = {
  /**
   * {
   *  "info": "Establece los elementos que forman el componente",
   *  "kind": "both",
   *  "beautifulName": "Elememtos del componente"
   * }
   */

  children: PropTypes.node,
  /**
   * {
   *  "info": "Establece el contenido del componente",
   *  "kind": "both",
   *  "beautifulName": "Elememtos del componente"
   * }
   */

  content: PropTypes.oneOf(Object.values(CONTENT)),
  /**
   * {
   *  "info": "Define el tema del componente en el array THEMES",
   *  "kind": "both",
   *  "beautifulName": "Elememtos del componente"
   * }
   */
  theme: PropTypes.oneOf(Object.values(THEMES)),

  /**
   * {
   *  "info": "Define el nombre del proyecto desde el que se pinta la página de error",
   *  "kind": "both",
   *  "beautifulName": "Nombre del proyecto"
   * }
   */
  projectId: PropTypes.string,
  /**
   * {
   *  "info": "Permite contribuir la infomación que será enviada a analítica tras la interacción (objeto para el taggeado)",
   *  "kind": "both",
   *  "beautifulName": "Propiedades tagueo"
   * }
   */
  taggingInfo: PropTypes.object,
  /**
   * {
   *  "info": "Props passed to content, used to customize the information that is showed",
   *  "kind": "both",
   *  "beautifulName": "Propiedades contenido"
   * }
   */
  contentProps: PropTypes.shape({
    button: PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
    title: PropTypes.string,
    msg: PropTypes.arrayOf(PropTypes.string),
    phone: PropTypes.string,
    errorName: PropTypes.string,
    errorCode: PropTypes.string,
    errorDetails: PropTypes.string,
  }),
  /**
   * {
   *  "info": "Sirve para la pantalla de network error cuando no tienes conexión",
   *  "kind": "both",
   *  "beautifulName": "reintentar"
   * }
   */
  retry: PropTypes.func,
};

Stopper.defaultProps = {
  children: null,
  theme: THEMES.BEACH,
  content: CONTENT.NOT_FOUND,
  projectId: 'unknown',
  taggingInfo: {},
  contentProps: {},
  contextData: null,
  retry: null,
};

export default Stopper;
